/**
 * Admin Dashboard Component
 * Hope SACCO - Main control center for administrators
 * Wrapped with AdminLayout so the global sidebar (incl. Manage Shares) appears here.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import globalApi from '../utils/globalFetchWrapper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [welcomeMessage, setWelcomeMessage] = useState('');

  useEffect(() => {
    // Get user from localStorage
    const storedUser = localStorage.getItem('hopeSaccoUser');
    if (!storedUser) {
      navigate('/login');
      return;
    }

    const userData = JSON.parse(storedUser);
    setUser(userData);

    // Show welcome message if redirected from login
    if (location.state?.message) {
      setWelcomeMessage(location.state.message);
      // Clear message after 5 seconds
      setTimeout(() => setWelcomeMessage(''), 5000);
    }

    // Fetch dashboard data
    fetchDashboardData();
  }, [navigate, location.pathname]);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      let mergedData = null;

      // 1) Try backend /dashboard/summary if you later wire a real summary controller
      try {
        const response = await globalApi.get('/dashboard/summary');

        if (response.status === 429) {
          console.warn('Rate limited on dashboard summary');
        } else if (response.ok) {
          const data = await response.json();
          // Expecting { data: { ...dashboardShape } }
          mergedData = data.data || null;
        }
      } catch (err) {
        if (err.message === 'Session expired') {
          return;
        }
        console.warn('Dashboard summary endpoint failed, will fall back to reports:', err);
      }

      // 2) Use Monthly Overview (which is already correct) to override key fields:
      //    - This month collections
      //    - Loans outstanding breakdown
      //    - Total members
      try {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const monthParam = `${yyyy}-${mm}`;

        const overviewRes = await globalApi.get(
          `/reports/monthly-overview?month=${monthParam}`
        );

        if (overviewRes.status === 429) {
          console.warn('Rate limited on monthly overview');
        } else if (overviewRes.ok) {
          const overviewJson = await overviewRes.json();
          if (overviewJson.success && overviewJson.data) {
            const ov = overviewJson.data;
            const totals = ov.totals || {};
            const members = ov.members || [];

            // Compute "true" loans outstanding from Monthly Overview totals
            const normalOutstanding =
              Number(totals.totalLoanBalance || 0) +
              Number(totals.totalAddNormalLoan || 0);

            const schoolOutstanding =
              Number(totals.totalSchoolLoan || 0) +
              Number(totals.totalAddSchoolLoan || 0);

            const emergencyOutstanding =
              Number(totals.totalEmergencyLoan || 0) +
              Number(totals.totalAddEmergencyLoan || 0);

            const totalOutstanding =
              normalOutstanding + schoolOutstanding + emergencyOutstanding;

            const thisMonthCollections = Number(
              totals.grandTotalDeductions || 0
            );

            const totalMembers = members.length;

            // Merge with any existing dashboard data from /dashboard/summary
            mergedData = {
              ...(mergedData || {}),
              totalMembers: totalMembers || mergedData?.totalMembers || 0,
              loansOutstanding: {
                normal: normalOutstanding,
                school: schoolOutstanding,
                emergency: emergencyOutstanding,
                total: totalOutstanding,
              },
              thisMonth: {
                ...(mergedData?.thisMonth || {}),
                collections: thisMonthCollections,
              },
            };
          }
        }
      } catch (err) {
        if (err.message === 'Session expired') {
          return;
        }
        console.warn('Monthly overview fallback failed:', err);
      }

      // 3) If everything failed, show zeros instead of fake demo data
      if (!mergedData) {
        mergedData = {
          totalMembers: 0,
          activeMembers: 0,
          totalLoans: {
            active: 0,
            pending: 0,
            total: 0,
          },
          loansOutstanding: {
            normal: 0,
            school: 0,
            emergency: 0,
            total: 0,
          },
          thisMonth: {
            collections: 0,
            disbursements: 0,
            newMembers: 0,
          },
          recentActivity: [],
        };
      }

      setDashboardData(mergedData);
    } catch (error) {
      console.error('Dashboard data error:', error);
      // Final safety net: show zeros
      setDashboardData({
        totalMembers: 0,
        activeMembers: 0,
        totalLoans: {
          active: 0,
          pending: 0,
          total: 0,
        },
        loansOutstanding: {
          normal: 0,
          school: 0,
          emergency: 0,
          total: 0,
        },
        thisMonth: {
          collections: 0,
          disbursements: 0,
          newMembers: 0,
        },
        recentActivity: [],
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(Number(amount || 0));

  // Use a safe default shape so UI never "blinks" even while loading
  const safeData = dashboardData || {
    totalMembers: 0,
    activeMembers: 0,
    totalLoans: {
      active: 0,
      pending: 0,
      total: 0,
    },
    loansOutstanding: {
      normal: 0,
      school: 0,
      emergency: 0,
      total: 0,
    },
    thisMonth: {
      collections: 0,
      disbursements: 0,
      newMembers: 0,
    },
    recentActivity: [],
  };

  return (
    <AdminLayout
      activePage="dashboard"

    >
      {/* Top Bar */}
      <header className="top-bar">
        <h1>Dashboard</h1>
        <div className="user-menu">
          <span className="user-greeting">
            Welcome, {user?.memberData?.personalInfo?.fullName || user?.username}
          </span>
          <div className="user-avatar">
            {(user?.memberData?.personalInfo?.fullName || user?.username)?.charAt(0).toUpperCase()}
          </div>
        </div>
      </header>

      {/* Small inline loading note instead of full-page spinner */}
      {isLoading && (
        <div style={{ marginTop: 8, marginBottom: 8, color: '#777', fontSize: 13 }}>
          Updating dashboard figures…
        </div>
      )}

      {/* Welcome Message */}
      {welcomeMessage && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          <span>{welcomeMessage}</span>
        </div>
      )}

      {/* Summary Cards */}
      <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="card card-blue">
          <div className="card-icon">👥</div>
          <div className="card-content">
            <h3>{safeData.totalMembers}</h3>
            <p>TOTAL MEMBERS</p>
            <span className="card-subtitle">{safeData.activeMembers || 0} active</span>
          </div>
          <button className="card-action" onClick={() => navigate('/admin/members')}>
            View All →
          </button>
        </div>

        <div className="card card-green">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <h3>{safeData.totalLoans?.active || 0}</h3>
            <p>ACTIVE LOANS</p>
            <span className="card-subtitle">
              {formatCurrency(safeData.loansOutstanding?.total || 0)} outstanding
            </span>
          </div>
          <button className="card-action" onClick={() => navigate('/admin/apply-loan')}>
            Apply Loan →
          </button>
        </div>

        <div className="card card-purple">
          <div className="card-icon">📈</div>
          <div className="card-content">
            <h3>{formatCurrency(safeData.thisMonth?.collections || 0)}</h3>
            <p>THIS MONTH'S COLLECTIONS</p>
            <span className="card-subtitle">
              {new Date().toLocaleString('en-GB', { month: 'long', year: 'numeric' })}
            </span>
          </div>
          <button className="card-action" onClick={() => navigate('/admin/reports')}>
            Details →
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="section">
        <h2 className="section-title">Quick Actions</h2>
        <div className="quick-actions">
          <button className="action-btn action-primary" onClick={() => navigate('/admin/apply-loan')}>
            <span className="action-icon">💵</span>
            <span>Apply for Loan</span>
          </button>
          <button className="action-btn action-secondary" onClick={() => navigate('/admin/members/new')}>
            <span className="action-icon">➕</span>
            <span>Add New Member</span>
          </button>
          <button className="action-btn action-secondary" onClick={() => navigate('/admin/monthly-processing')}>
            <span className="action-icon">🔄</span>
            <span>Process Monthly Deductions</span>
          </button>
          <button className="action-btn action-secondary" onClick={() => navigate('/admin/reports')}>
            <span className="action-icon">📊</span>
            <span>View Reports</span>
          </button>
          <button
            className="action-btn action-secondary"
            onClick={() => navigate('/admin/loans/import-historical')}
          >
            <span className="action-icon">📥</span>
            <span>Import Historical Loan</span>
          </button>
          <button
            className="action-btn action-secondary"
            onClick={() => navigate('/admin/reports/member-statement')}
          >
            <span className="action-icon">📄</span>
            <span>Generate Member Statement</span>
          </button>
          <button
            className="action-btn action-secondary"
            onClick={() => navigate('/admin/reports/monthly-overview')}
          >
            <span className="action-icon">📅</span>
            <span>Monthly Overview</span>
          </button>
          <button
            className="action-btn action-secondary"
            onClick={() => navigate('/admin/members/manage-shares')}
          >
            <span className="action-icon">💹</span>
            <span>Manage Shares</span>
          </button>
          <button
            className="action-btn action-secondary"
            onClick={() => navigate('/admin/members/bulk-accounts')}
          >
            <span className="action-icon">👥</span>
            <span>Bulk Create Accounts</span>
          </button>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="dashboard-grid">
        {/* Recent Activity */}
        <div className="section">
          <h2 className="section-title">Recent Activity</h2>
          <div className="activity-list">
            {safeData.recentActivity && safeData.recentActivity.length > 0 ? (
              safeData.recentActivity.map((activity) => (
                <div key={activity.id} className="activity-item">
                  <div className="activity-icon">
                    {activity.type === 'loan_application' && '📝'}
                    {activity.type === 'loan_disbursement' && '💸'}
                    {activity.type === 'member_registration' && '👤'}
                  </div>
                  <div className="activity-details">
                    <p className="activity-title">
                      {activity.type === 'loan_application' && `Loan Application - ${activity.member}`}
                      {activity.type === 'loan_disbursement' && `Loan Disbursed - ${activity.member}`}
                      {activity.type === 'member_registration' && `New Member - ${activity.member}`}
                    </p>
                    <p className="activity-meta">
                      {activity.amount && formatCurrency(activity.amount)}{' • '}
                      {new Date(activity.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p style={{ color: '#777', fontSize: 13 }}>No recent activity yet.</p>
            )}
          </div>
        </div>

        {/* Loan Breakdown */}
        <div className="section">
          <h2 className="section-title">Loans Outstanding</h2>
          <div className="breakdown-list">
            <div className="breakdown-item">
              <div className="breakdown-label">
                <span className="breakdown-dot blue"></span>
                <span>Normal Loans</span>
              </div>
              <div className="breakdown-value">
                {formatCurrency(safeData.loansOutstanding?.normal || 0)}
              </div>
            </div>

            <div className="breakdown-item">
              <div className="breakdown-label">
                <span className="breakdown-dot orange"></span>
                <span>School Fees Loans</span>
              </div>
              <div className="breakdown-value">
                {formatCurrency(safeData.loansOutstanding?.school || 0)}
              </div>
            </div>

            <div className="breakdown-item">
              <div className="breakdown-label">
                <span className="breakdown-dot red"></span>
                <span>Emergency Loans</span>
              </div>
              <div className="breakdown-value">
                {formatCurrency(safeData.loansOutstanding?.emergency || 0)}
              </div>
            </div>

            <div className="breakdown-item breakdown-total">
              <div className="breakdown-label">
                <strong>Total Outstanding</strong>
              </div>
              <div className="breakdown-value">
                <strong>{formatCurrency(safeData.loansOutstanding?.total || 0)}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
